import { REQUEST_ID_HEADER } from '@/shared/utils/logger/constants';
import { generateRequestId } from '@/shared/utils/logger/generate-request-id';

import {
	isSsrDebugEnabled,
	parseDebugBody,
	parseRequestBody,
	pickRequestId,
	pushSsrDebugEntry,
} from './ssr-debug';

const _nativeFetch = fetch;

/**
 * Обёртка над `fetch` для серверных вызовов `api`:
 * - добавляет `X-Request-Id` для сквозного трейсинга с бэкендом;
 * - при `x-algocode-ssr-debug` копит request/response для дампа в browser console.
 *
 * НЕ импортировать из client-компонентов.
 */
export const fetchWithRequestId: typeof fetch = async (input, init) => {
	const headers = new Headers(init?.headers ?? undefined);

	if (!headers.has(REQUEST_ID_HEADER)) {
		const id = await resolveServerRequestId();
		if (id) headers.set(REQUEST_ID_HEADER, id);
	}

	const debug = await isSsrDebugEnabled();
	if (!debug) {
		return _nativeFetch(input, { ...init, headers });
	}

	const method = (init?.method ?? 'GET').toUpperCase();
	const url =
		typeof input === 'string'
			? input
			: input instanceof URL
				? input.toString()
				: input.url;
	const requestBody = parseRequestBody(init?.body ?? null);
	const started = Date.now();

	try {
		const response = await _nativeFetch(input, { ...init, headers });
		const responseBody = await parseDebugBody(response.clone());

		pushSsrDebugEntry({
			request: {
				method,
				url,
				requestId: pickRequestId(headers),
				body: requestBody,
			},
			response: {
				status: response.status,
				ok: response.ok,
				durationMs: Date.now() - started,
				body: responseBody,
			},
		});

		return response;
	} catch (error) {
		pushSsrDebugEntry({
			request: {
				method,
				url,
				requestId: pickRequestId(headers),
				body: requestBody,
			},
			response: {
				status: null,
				ok: null,
				durationMs: Date.now() - started,
				body: null,
				error: error instanceof Error ? error.message : String(error),
			},
		});
		throw error;
	}
};

const resolveServerRequestId = async (): Promise<string | undefined> => {
	try {
		const { headers } = await import('next/headers');
		const h = await headers();
		return h.get(REQUEST_ID_HEADER) ?? generateRequestId();
	} catch {
		return generateRequestId();
	}
};
